import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/ui";
import { Button } from "@/components/ui/UIElements/Button";
import { Radio } from "@/components/ui/UIElements/SelectionControl";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { useFacebookSDK } from "@/hooks/useFacebookSDK";
import {
  useInitWhatsAppOAuth,
  useCompleteWhatsAppOAuth,
  useDisconnectWhatsAppOAuth,
  useDownloadWhatsAppPin,
  useVerifyWhatsAppRegistration,
  useDoctorWhatsApp,
} from "@/hooks/useAiAgents";
import WhatsAppDisconnectModal from "./WhatsAppDisconnectModal";
import Modal from "@/components/ui/UIElements/Modal";

const AUTH_TYPE_MANUAL = "manual";
const AUTH_TYPE_BUSINESS_LOGIN = "business_login";

/**
 * Whether Embedded Signup ended with nothing to complete: cancelled, or
 * failed. Shows the message Meta reports, if any; an error without one
 * shows the fallback.
 */
const isSignupInterrupted = (sessionInfo, toast, errorFallback) => {
  const event = sessionInfo?.event;
  if (event !== "CANCEL" && event !== "ERROR") {
    return false;
  }
  const message =
    sessionInfo?.data?.error_message ||
    (event === "ERROR" ? errorFallback : null);
  if (message) {
    toast.error(message);
  }
  return true;
};

/**
 * WhatsAppAuthSection - Handles WhatsApp authentication type selection and OAuth flow
 *
 * @param {Object} props
 * @param {Object} props.agent - Full agent object with configured, misconfigured_reason, provider_settings
 * @param {string} props.authType - Current auth_type value
 * @param {Function} props.onAuthTypeChange - Callback when auth_type changes
 * @param {Function} props.onCreateAgent - Optional callback to create agent before OAuth (for create modal flow)
 * @param {boolean} props.autoConnect - If true, automatically start OAuth on mount
 * @param {boolean} props.readOnly - If true, disable all interactions
 */
const WhatsAppAuthSection = ({
  agent,
  authType = AUTH_TYPE_MANUAL,
  onAuthTypeChange,
  onCreateAgent,
  onSaveBeforeConnect,
  autoConnect = false,
  readOnly = false,
  editedPin,
}) => {
  const { t } = useTranslation("aiAgents");
  const toast = useToast();
  const { loadSDK, login, isLoading: isLoadingSDK } = useFacebookSDK();

  const initOAuthMutation = useInitWhatsAppOAuth();
  const completeOAuthMutation = useCompleteWhatsAppOAuth();
  const disconnectMutation = useDisconnectWhatsAppOAuth();
  const downloadPinMutation = useDownloadWhatsAppPin();
  const verifyRegistrationMutation = useVerifyWhatsAppRegistration();
  const doctorMutation = useDoctorWhatsApp();

  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showAuthTypeChangeModal, setShowAuthTypeChangeModal] = useState(false);
  const [pendingAuthType, setPendingAuthType] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showPinConfirmModal, setShowPinConfirmModal] = useState(false);
  const changePinRef = useRef(null);
  const keepBusinessLoginRef = useRef(null);

  const providerSettings = agent?.provider_settings || {};
  const isConfigured = agent?.configured === true;
  const misconfiguredReason = agent?.misconfigured_reason;
  const isBusinessLogin = authType === AUTH_TYPE_BUSINESS_LOGIN;
  const phoneNumber = providerSettings.phone_number;
  const countryCode = providerSettings.country_code;
  const connectedAt = providerSettings.connected_at;
  const pin = providerSettings.pin;
  const pinDownloadedAt = providerSettings.pin_downloaded_at;
  const registrationStatus = providerSettings.registration_status;

  // Determine connection status
  const getConnectionStatus = () => {
    if (isBusinessLogin && isConfigured) {
      return "connected";
    }
    if (isBusinessLogin && misconfiguredReason) {
      return "error";
    }
    if (isBusinessLogin && !isConfigured) {
      return "notConnected";
    }
    return null;
  };

  const connectionStatus = getConnectionStatus();

  // Handle auth type change
  const handleAuthTypeSelect = (newAuthType) => {
    if (readOnly) return;
    if (newAuthType === authType) return;

    // If changing from business_login to manual and connected, show confirmation
    if (authType === AUTH_TYPE_BUSINESS_LOGIN && isConfigured) {
      setPendingAuthType(newAuthType);
      setShowAuthTypeChangeModal(true);
      return;
    }

    onAuthTypeChange?.(newAuthType);
  };

  const handleAuthTypeChangeConfirm = () => {
    setShowAuthTypeChangeModal(false);
    if (pendingAuthType) {
      onAuthTypeChange?.(pendingAuthType);
      setPendingAuthType(null);
    }
  };

  const handleAuthTypeChangeCancel = () => {
    setShowAuthTypeChangeModal(false);
    setPendingAuthType(null);
  };

  // Handle OAuth connect
  const handleConnect = useCallback(async () => {
    let agentId = agent?.id;

    // If no agent ID and onCreateAgent is provided, create the agent first
    if (!agentId && onCreateAgent) {
      try {
        const newAgent = await onCreateAgent();
        agentId = newAgent?.id;
        if (!agentId) {
          // Creation failed or was cancelled
          return;
        }
      } catch {
        // Error already handled by onCreateAgent
        return;
      }
    }

    if (!agentId) return;

    // Save any unsaved agent changes before starting OAuth
    if (onSaveBeforeConnect) {
      const saved = await onSaveBeforeConnect();
      if (!saved) return;
    }

    setIsConnecting(true);
    try {
      // Step 1: Initialize OAuth flow
      const initResult = await initOAuthMutation.mutateAsync({
        agentId,
        authStrategy: "jssdk",
      });

      const { facebook } = initResult;
      if (!facebook?.app_id || !facebook?.config_id) {
        throw new Error("Invalid OAuth configuration received");
      }

      // Step 2: Load Facebook SDK
      await loadSDK(facebook.app_id);

      // Step 3: Open Facebook Login popup
      const loginOptions = {
        config_id: facebook.config_id,
        response_type: "code",
        override_default_response_type: true,
        scope: facebook.scope,
        extras: { setup: {} },
      };
      const response = await login(loginOptions);

      // Check if the user cancelled or hit an error during the embedded signup flow
      if (
        isSignupInterrupted(
          response.sessionInfo,
          toast,
          t("whatsappOAuth.toasts.initError"),
        )
      ) {
        return;
      }

      // Step 4: Send code and session info to backend to complete OAuth.
      // Without the session info the backend finds the WABA and phone
      // number from the token.
      if (response.authResponse?.code) {
        const sessionData = response.sessionInfo?.data;
        const completeData = {
          agentId,
          code: response.authResponse.code,
          phone_number_id: sessionData?.phone_number_id,
          waba_id: sessionData?.waba_id,
        };

        await completeOAuthMutation.mutateAsync(completeData);
        handleApiSuccess(t("whatsappOAuth.toasts.connectSuccess"), toast);
      }
    } catch (error) {
      if (error.message === "Login cancelled or failed") {
        // User cancelled - no error toast needed
        return;
      }
      handleApiError(error, toast, {
        title: t("whatsappOAuth.toasts.connectError"),
        defaultMessage: t("whatsappOAuth.toasts.initError"),
      });
    } finally {
      setIsConnecting(false);
    }
  }, [
    agent?.id,
    onCreateAgent,
    onSaveBeforeConnect,
    initOAuthMutation,
    completeOAuthMutation,
    loadSDK,
    login,
    toast,
    t,
  ]);

  // Wrapper that checks for PIN before starting connect flow
  const handleConnectWithPinCheck = useCallback(() => {
    if (!editedPin) {
      setShowPinConfirmModal(true);
      return;
    }
    handleConnect();
  }, [editedPin, handleConnect]);

  // Handle PIN download
  const handleDownloadPin = useCallback(async () => {
    if (!agent?.id) return;

    try {
      await downloadPinMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("whatsappOAuth.info.downloadPinSuccess"), toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("whatsappOAuth.info.downloadPinError"),
      });
    }
  }, [agent?.id, downloadPinMutation, toast, t]);

  // Handle verify registration
  const handleVerifyRegistration = useCallback(async () => {
    if (!agent?.id) return;

    try {
      await verifyRegistrationMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(
        t("whatsappOAuth.info.verifyRegistrationSuccess"),
        toast,
      );
    } catch (error) {
      handleApiError(error, toast, {
        title: t("whatsappOAuth.info.verifyRegistrationError"),
      });
    }
  }, [agent?.id, verifyRegistrationMutation, toast, t]);

  // Handle OAuth disconnect
  const handleDisconnect = useCallback(async () => {
    if (!agent?.id) return;

    try {
      await disconnectMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("whatsappOAuth.toasts.disconnectSuccess"), toast);
      setShowDisconnectModal(false);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("whatsappOAuth.toasts.disconnectError"),
      });
    }
  }, [agent?.id, disconnectMutation, toast, t]);

  // Handle doctor (repair connection)
  const handleDoctor = useCallback(async () => {
    if (!agent?.id) return;

    try {
      const response = await doctorMutation.mutateAsync({ agentId: agent.id });
      const messages = Array.isArray(response?.result)
        ? response.result
        : [response?.result || t("whatsappOAuth.toasts.doctorSuccess")];
      const isError = response?.error === true;

      messages.forEach((msg) => {
        if (isError) {
          toast.error(msg);
        } else {
          toast.success(msg);
        }
      });
    } catch (error) {
      handleApiError(error, toast, {
        title: t("whatsappOAuth.toasts.doctorError"),
      });
    }
  }, [agent?.id, doctorMutation, toast, t]);

  // Handle logout + init + complete (for account_blocked)
  const handleLogoutAndReconnect = useCallback(async () => {
    if (!agent?.id) return;

    setIsConnecting(true);
    try {
      // Step 1: Logout
      await disconnectMutation.mutateAsync({ agentId: agent.id });

      // Step 2-4: Same as handleConnect (init + SDK + complete)
      const initResult = await initOAuthMutation.mutateAsync({
        agentId: agent.id,
        authStrategy: "jssdk",
      });

      const { facebook } = initResult;
      if (!facebook?.app_id || !facebook?.config_id) {
        throw new Error("Invalid OAuth configuration received");
      }

      await loadSDK(facebook.app_id);

      const loginOptions = {
        config_id: facebook.config_id,
        response_type: "code",
        override_default_response_type: true,
        scope: facebook.scope,
        extras: { setup: {} },
      };
      const response = await login(loginOptions);

      if (
        isSignupInterrupted(
          response.sessionInfo,
          toast,
          t("whatsappOAuth.toasts.initError"),
        )
      ) {
        return;
      }

      if (response.authResponse?.code) {
        const sessionData = response.sessionInfo?.data;
        const completeData = {
          agentId: agent.id,
          code: response.authResponse.code,
          phone_number_id: sessionData?.phone_number_id,
          waba_id: sessionData?.waba_id,
        };

        await completeOAuthMutation.mutateAsync(completeData);
        handleApiSuccess(t("whatsappOAuth.toasts.connectSuccess"), toast);
      }
    } catch (error) {
      if (error.message === "Login cancelled or failed") return;
      handleApiError(error, toast, {
        title: t("whatsappOAuth.toasts.connectError"),
        defaultMessage: t("whatsappOAuth.toasts.initError"),
      });
    } finally {
      setIsConnecting(false);
    }
  }, [
    agent?.id,
    disconnectMutation,
    initOAuthMutation,
    completeOAuthMutation,
    loadSDK,
    login,
    toast,
    t,
  ]);

  // Get action config based on misconfigured_reason
  const getErrorAction = (reason) => {
    switch (reason) {
      case "pending_oauth":
      case "logged_out":
        return {
          handler: handleConnectWithPinCheck,
          label: t("whatsappOAuth.connectButton"),
          icon: "MessageCircle",
        };
      case "missing_access_token":
      case "invalid_access_token":
      case "revoked_by_user":
      case "auth_type_changed":
        return {
          handler: handleConnectWithPinCheck,
          label: t("whatsappOAuth.reconnectButton"),
          icon: "RefreshCw",
        };
      case "missing_phone_number_id":
        return {
          handler: handleDoctor,
          label: t("whatsappOAuth.completeSetupButton"),
          icon: "Settings",
        };
      case "missing_pin":
      case "invalid_pin":
        return {
          handler: handleDoctor,
          label: t("whatsappOAuth.diagnose.button"),
          icon: "Stethoscope",
          showDisconnect: true,
        };
      case "pin_rate_limited":
      case "phone_reverification_needed":
      case "phone_not_registered":
      case "certificate_error":
      case "registration_failed":
      case "registration_rate_limited":
        return {
          handler: handleDoctor,
          label: t("whatsappOAuth.retryButton"),
          icon: "RefreshCw",
        };
      case "account_blocked":
        return {
          handler: handleLogoutAndReconnect,
          label: t("whatsappOAuth.connectAnotherButton"),
          icon: "UserPlus",
        };
      case "pending_registration":
        return {
          handler: handleVerifyRegistration,
          label: t("whatsappOAuth.checkStatusButton"),
          icon: "Search",
        };
      default:
        return {
          handler: handleConnectWithPinCheck,
          label: t("whatsappOAuth.reconnectButton"),
          icon: "RefreshCw",
        };
    }
  };

  // Format connected date
  const formatConnectedAt = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return null;
    }
  };

  // Get error message for misconfigured reason
  const getErrorMessage = (reason) => {
    if (!reason) return null;
    const key = `whatsappOAuth.errors.${reason}`;
    const translated = t(key);
    return translated !== key ? translated : reason;
  };

  const isLoading =
    isConnecting ||
    isLoadingSDK ||
    initOAuthMutation.isPending ||
    completeOAuthMutation.isPending ||
    disconnectMutation.isPending ||
    doctorMutation.isPending;
  const isDownloadingPin = downloadPinMutation.isPending;
  const isVerifying = verifyRegistrationMutation.isPending;

  // Auto-connect on mount when autoConnect is true
  const hasAutoConnected = useRef(false);
  useEffect(() => {
    if (
      autoConnect &&
      !hasAutoConnected.current &&
      agent?.id &&
      isBusinessLogin &&
      !isConfigured
    ) {
      hasAutoConnected.current = true;
      handleConnectWithPinCheck();
    }
  }, [
    autoConnect,
    agent?.id,
    isBusinessLogin,
    isConfigured,
    handleConnectWithPinCheck,
  ]);

  return (
    <div className="ordering-ai-agent-form space-y-4">
      {/* Auth Type Selector */}
      <div>
        <label className="text-muted-foreground mb-2 block text-[length:var(--font-size-product-ui)] font-medium">
          {t("whatsappOAuth.authTypeTitle")}
        </label>
        <div className="space-y-2">
          {/* Business Login Option (Recommended) */}
          <label
            className={`flex cursor-pointer items-start rounded-lg border p-3 transition-colors ${
              authType === AUTH_TYPE_BUSINESS_LOGIN
                ? "border-primary bg-accent/20"
                : "border-border bg-card hover:border-border"
            } ${readOnly ? "cursor-not-allowed opacity-50" : ""}`}
          >
            <Radio
              name="authType"
              value={AUTH_TYPE_BUSINESS_LOGIN}
              checked={authType === AUTH_TYPE_BUSINESS_LOGIN}
              onChange={() => handleAuthTypeSelect(AUTH_TYPE_BUSINESS_LOGIN)}
              disabled={readOnly || isLoading}
              className="mt-0.5"
            />
            <div className="ms-3 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-foreground text-sm font-medium">
                  {t("whatsappOAuth.authTypeBusinessLogin")}
                </span>
                <span className="text-success rounded bg-[var(--success-bg)] px-1.5 py-0.5 text-xs">
                  {t("whatsappOAuth.authTypeBusinessLoginDesc")
                    .split("(")[1]
                    ?.replace(")", "") || "recommended"}
                </span>
              </div>
              <span className="text-muted-foreground block text-xs">
                {t("whatsappOAuth.authTypeBusinessLoginDesc").split("(")[0]}
              </span>
            </div>
          </label>

          {/* Manual Option */}
          <label
            className={`flex cursor-pointer items-start rounded-lg border p-3 transition-colors ${
              authType === AUTH_TYPE_MANUAL
                ? "border-primary bg-accent/20"
                : "border-border bg-card hover:border-border"
            } ${readOnly ? "cursor-not-allowed opacity-50" : ""}`}
          >
            <Radio
              name="authType"
              value={AUTH_TYPE_MANUAL}
              checked={authType === AUTH_TYPE_MANUAL}
              onChange={() => handleAuthTypeSelect(AUTH_TYPE_MANUAL)}
              disabled={readOnly || isLoading}
              className="mt-0.5"
            />
            <div className="ms-3">
              <span className="text-foreground block text-sm font-medium">
                {t("whatsappOAuth.authTypeManual")}
              </span>
              <span className="text-muted-foreground block text-xs">
                {t("whatsappOAuth.authTypeManualDesc")}
              </span>
            </div>
          </label>
        </div>
      </div>

      {/* Business Login Connection Section */}
      {isBusinessLogin && (
        <div className="border-border bg-card/50 rounded-lg border p-4">
          {/* Connected State */}
          {connectionStatus === "connected" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="border-success/30 text-success inline-flex items-center rounded-full border bg-[var(--success-bg)] px-2 py-0.5 text-xs font-medium">
                    <Icon name="Check" className="me-1 h-3 w-3" />
                    {t("whatsappOAuth.status.connected")}
                  </span>
                </div>
                <Button
                  type="button"
                  emphasis="ghost"
                  intent="danger"
                  size="touch"
                  onClick={() => setShowDisconnectModal(true)}
                  disabled={readOnly || isLoading}
                >
                  {t("whatsappOAuth.disconnectButton")}
                </Button>
              </div>

              {/* Connection Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {(countryCode || phoneNumber) && (
                  <div>
                    <span className="text-muted-foreground">
                      {t("whatsappOAuth.info.phoneNumber")}:
                    </span>
                    <span className="text-foreground ms-2">
                      {countryCode ? `+${countryCode} ` : ""}
                      {phoneNumber || "-"}
                    </span>
                  </div>
                )}
                {connectedAt && (
                  <div>
                    <span className="text-muted-foreground">
                      {t("whatsappOAuth.info.connectedAt")}:
                    </span>
                    <span className="text-foreground ms-2">
                      {formatConnectedAt(connectedAt)}
                    </span>
                  </div>
                )}
              </div>

              {/* Diagnose connection */}
              <div className="border-border bg-card flex items-center justify-between rounded-lg border p-3">
                <span className="text-muted-foreground text-sm">
                  {t("whatsappOAuth.diagnose.message")}
                </span>
                <Button
                  type="button"
                  emphasis="outline"
                  intent="neutral"
                  size="touch"
                  onClick={handleDoctor}
                  disabled={readOnly || isLoading}
                >
                  {doctorMutation.isPending ? (
                    <>
                      <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      {t("whatsappOAuth.processing")}
                    </>
                  ) : (
                    <>
                      <Icon name="Stethoscope" className="h-3.5 w-3.5" />
                      {t("whatsappOAuth.diagnose.button")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Error State */}
          {connectionStatus === "error" &&
            misconfiguredReason &&
            (() => {
              const errorAction = getErrorAction(misconfiguredReason);
              return (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="border-destructive/30 text-destructive inline-flex items-center rounded-full border bg-[var(--error-bg)] px-2 py-0.5 text-xs font-medium">
                      <Icon name="AlertCircle" className="me-1 h-3 w-3" />
                      {t("whatsappOAuth.status.error")}
                    </span>
                  </div>
                  <p className="text-destructive text-sm">
                    {getErrorMessage(misconfiguredReason)}
                  </p>

                  {!errorAction.messageOnly && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        emphasis="solid"
                        intent="brand"
                        size="touch"
                        onClick={errorAction.handler}
                        disabled={readOnly || isLoading}
                      >
                        {isLoading ? (
                          <>
                            <div className="me-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent motion-reduce:animate-none" />
                            {t("whatsappOAuth.processing")}
                          </>
                        ) : (
                          <>
                            <Icon
                              name={errorAction.icon}
                              className="me-2 h-4 w-4"
                            />
                            {errorAction.label}
                          </>
                        )}
                      </Button>
                      {errorAction.showDisconnect && (
                        <Button
                          type="button"
                          emphasis="outline"
                          intent="danger"
                          size="touch"
                          onClick={() => setShowDisconnectModal(true)}
                          disabled={readOnly || isLoading}
                        >
                          <Icon name="LogOut" className="me-2 h-4 w-4" />
                          {t("whatsappOAuth.disconnectButton")}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

          {/* Not Connected State */}
          {connectionStatus === "notConnected" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="border-border/30 bg-card/50 text-muted-foreground inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium">
                  {t("whatsappOAuth.status.notConnected")}
                </span>
              </div>
              <Button
                type="button"
                variant="default"
                size="touch"
                onClick={handleConnectWithPinCheck}
                disabled={readOnly || isLoading}
                className="w-full bg-[#1877F2] text-white hover:bg-[#166FE5]"
              >
                {isLoading ? (
                  <>
                    <div className="me-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent motion-reduce:animate-none" />
                    {t("whatsappOAuth.connecting")}
                  </>
                ) : (
                  <>
                    <Icon name="MessageCircle" className="me-2 h-4 w-4" />
                    {t("whatsappOAuth.connectButton")}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* PIN Info & Download */}
          {pin && providerSettings.registration_status != null && (
            <div className="border-border bg-card mt-3 flex items-center justify-between rounded-lg border p-3">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">
                  {t("whatsappOAuth.info.pin")}:
                </span>
                <span className="text-foreground text-sm font-medium">
                  {pin}
                </span>
                {!pinDownloadedAt && (
                  <span title={t("whatsappOAuth.info.pinNotDownloaded")}>
                    <Icon
                      name="AlertTriangle"
                      className="text-warning h-4 w-4"
                    />
                  </span>
                )}
              </div>
              <Button
                type="button"
                emphasis="outline"
                intent="neutral"
                size="touch"
                onClick={handleDownloadPin}
                disabled={readOnly || isDownloadingPin}
              >
                {isDownloadingPin ? (
                  <>
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {t("whatsappOAuth.info.downloadingPin")}
                  </>
                ) : (
                  <>
                    <Icon name="Download" className="h-3.5 w-3.5" />
                    {t("whatsappOAuth.info.downloadPin")}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Verify Registration */}
          {registrationStatus != null &&
            registrationStatus.toUpperCase() !== "CONNECTED" && (
              <Button
                type="button"
                emphasis="outline"
                intent="brand"
                size="touch"
                onClick={handleVerifyRegistration}
                disabled={readOnly || isVerifying}
                className="mt-3 w-full"
              >
                {isVerifying ? (
                  <>
                    <div className="border-primary h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
                    {t("whatsappOAuth.info.verifyingRegistration")}
                  </>
                ) : (
                  <>
                    <Icon name="RefreshCw" className="h-3.5 w-3.5" />
                    {["FLAGGED", "RESTRICTED"].includes(
                      registrationStatus.toUpperCase(),
                    )
                      ? t("whatsappOAuth.info.refreshStatus")
                      : t("whatsappOAuth.info.verifyRegistration")}
                  </>
                )}
              </Button>
            )}
        </div>
      )}

      {/* Disconnect Confirmation Modal */}
      <WhatsAppDisconnectModal
        isOpen={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onConfirm={handleDisconnect}
        isLoading={disconnectMutation.isPending}
      />

      {/* PIN Confirmation Modal */}
      <Modal
        isOpen={showPinConfirmModal}
        onClose={() => setShowPinConfirmModal(false)}
        title={t("whatsappOAuth.pinConfirmModal.title")}
        showCloseButton={false}
        closeOnBackdrop={false}
        initialFocusRef={changePinRef}
        maxWidth="max-w-md"
      >
        <div className="border-warning mb-4 rounded-lg border bg-[var(--warning-bg)] p-3">
          <div className="flex items-start gap-3">
            <Icon
              name="AlertTriangle"
              className="text-warning mt-0.5 h-5 w-5 flex-shrink-0"
            />
            <p className="text-warning text-sm">
              {t("whatsappOAuth.pinConfirmModal.message")}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            emphasis="outline"
            intent="neutral"
            size="touch"
            onClick={() => {
              setShowPinConfirmModal(false);
              handleConnect();
            }}
          >
            {t("whatsappOAuth.pinConfirmModal.continue")}
          </Button>
          <Button
            ref={changePinRef}
            type="button"
            emphasis="solid"
            intent="brand"
            size="touch"
            onClick={() => setShowPinConfirmModal(false)}
          >
            {t("whatsappOAuth.pinConfirmModal.changePin")}
          </Button>
        </div>
      </Modal>

      {/* Auth Type Change Confirmation Modal */}
      <Modal
        isOpen={showAuthTypeChangeModal}
        onClose={handleAuthTypeChangeCancel}
        title={t("whatsappOAuth.changeAuthTypeModal.title")}
        showCloseButton={false}
        closeOnBackdrop={false}
        initialFocusRef={keepBusinessLoginRef}
        maxWidth="max-w-md"
      >
        <div className="border-warning mb-4 rounded-lg border bg-[var(--warning-bg)] p-3">
          <div className="flex items-start gap-3">
            <Icon
              name="AlertTriangle"
              className="text-warning mt-0.5 h-5 w-5 flex-shrink-0"
            />
            <p className="text-warning text-sm">
              {t("whatsappOAuth.changeAuthTypeModal.message")}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button
            ref={keepBusinessLoginRef}
            type="button"
            emphasis="outline"
            intent="neutral"
            size="touch"
            onClick={handleAuthTypeChangeCancel}
          >
            {t("whatsappOAuth.changeAuthTypeModal.cancel")}
          </Button>
          <Button
            type="button"
            emphasis="solid"
            intent="warning"
            size="touch"
            onClick={handleAuthTypeChangeConfirm}
          >
            {t("whatsappOAuth.changeAuthTypeModal.confirm")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default WhatsAppAuthSection;
